/**
 * AgentPanel — The main agentic AI chat experience
 *
 * Single interleaved transcript stream with:
 * - Always-active input (queue messages during generation)
 * - Live markdown streaming
 * - Inline thinking, tool calls, errors
 * - Model selector & settings in header
 * - Cost tracking
 * - Auto-scroll with jump-to-bottom
 */
import { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect, type CSSProperties } from 'react'
import type { Workspace } from '../engine/types'
import {
    getProviderApiMode,
    getNormalizedMcpServers,
    isMcpServerEnabled,
    isMcpToolEnabled,
    type AgentConfig,
    type AgentRunStatus,
    type AgentMcpServerConfig,
    type NormalizedAgentMcpServerConfig,
    type TranscriptEvent,
} from '../engine/agentTypes'
import { useAgent, type QueuedMessage } from '../hooks/useAgent'
import { RemoteMcpClient } from '../engine/mcp/remoteClient'
import {
    aggregateServerMetrics,
    buildMcpStatusUrl,
    extractRecentToolCalls,
    getSecureBlobStatusHeaders,
    type McpServerMetrics,
    type McpStatusSnapshot,
    type McpToolCallEntry,
} from '../engine/mcp/status'
import { SECUREBLOB_TOOL_SETTINGS, isSecureBlobToolEnabled } from '../engine/providers/openai-chat-completions/tools'
import { MarkdownContent, TranscriptMessage, TranscriptMetricsBar, type MessageMetrics } from './agent/TranscriptMessage'
import { ModelPicker } from './agent/ModelPicker'
import { CostBadge } from './agent/CostBadge'
import { FileRefInput } from './agent/FileRefInput'
import { ArrowUp, FilePenLine, History, Maximize2, Minimize2, PanelRightOpen, Settings2 } from 'lucide-react'
import './AgentPanel.css'

interface AgentPanelProps {
    agentPath: string
    workspace: Workspace
    onOpenFile?: (path: string) => void
    onEditConfig?: () => void
}

interface McpToolState {
    name: string
    title?: string
    description?: string
    enabled: boolean
}

interface McpServerState {
    name: string
    enabled: boolean
    proxy: boolean
    status: 'disabled' | 'loading' | 'ready' | 'error'
    host?: string
    tools: McpToolState[]
    error?: string
    metrics?: McpServerMetrics
    recentToolCalls?: McpToolCallEntry[]
    statusRequestKey?: string
    liveEventsUrl?: string
    liveEventsHeaders?: Record<string, string>
    supportsWorkspaceEventStream?: boolean
}

interface McpStatusRequest {
    url: string
    headers: Record<string, string>
    requestKey: string
}

interface McpStatusFetchResult {
    metrics?: McpServerMetrics
    recentToolCalls?: McpToolCallEntry[]
    rateLimited?: boolean
}

interface CachedMcpServerStatus {
    requestKey: string
    metrics: McpServerMetrics
    recentToolCalls?: McpToolCallEntry[]
}
interface McpSummary {
    totalServers: number
    enabledServers: number
    readyServers: number
    loadingServers: number
    errorServers: number
    totalTools: number
    enabledTools: number
}

interface WorkspaceToolState {
    name: string
    title: string
    description: string
    enabled: boolean
    mode: 'read' | 'write'
}

interface RunTimelineItem {
    key: string
    kind: 'prompt' | 'thinking' | 'tool' | 'message' | 'error' | 'image'
    timestamp: number
    title: string
    detail?: string
    status?: string
    durationMs?: number
}

interface DerivedRun {
    id: string
    prompt: string
    startedAt: number
    endedAt: number
    status: AgentRunStatus
    metrics: MessageMetrics
    eventIndices: number[]
    assistantIndices: number[]
    thinkingCount: number
    toolCount: number
    errorCount: number
    fileChanges: number
    timeline: RunTimelineItem[]
    rawEvents: TranscriptEvent[]
}

interface TranscriptHistoryEntry {
    id: string
    path: string
    startedAt: number
    label: string
    detail: string
    isCurrent: boolean
}

function getMergedMcpEntries(config: AgentConfig): Record<string, AgentMcpServerConfig | string> {
    return {
        ...(config.mcpServers ?? {}),
        ...(config.mcp ?? {}),
    }
}

function getMcpRootKey(config: AgentConfig, serverName: string): 'mcp' | 'mcpServers' {
    if (config.mcp && (serverName in config.mcp || !(config.mcpServers && serverName in config.mcpServers))) {
        return 'mcp'
    }
    return 'mcpServers'
}

function toEditableMcpServerConfig(entry: AgentMcpServerConfig | string | undefined): AgentMcpServerConfig {
    if (!entry) return {}
    if (typeof entry === 'string') return { url: entry }
    return {
        ...entry,
        tools: entry.tools ? { ...entry.tools } : undefined,
    }
}

function getConfiguredToolStates(entry: AgentMcpServerConfig | string | undefined): McpToolState[] {
    if (!entry || typeof entry === 'string' || !entry.tools) return []
    return Object.entries(entry.tools).map(([name, toolConfig]) => ({
        name,
        enabled: typeof toolConfig === 'boolean' ? toolConfig : toolConfig?.enabled !== false,
    }))
}

function isMcpProxyEnabled(entry: AgentMcpServerConfig | string | undefined): boolean {
    return typeof entry === 'object' && !!entry && entry.proxy === true
}

function getRawMcpServerUrl(entry: AgentMcpServerConfig | string | undefined): string | undefined {
    if (typeof entry === 'string') {
        return entry
    }
    return entry?.url?.toString() || entry?.serverUrl?.toString()
}

function getDisplayHost(value: string | undefined): string {
    const trimmed = value?.trim()
    if (!trimmed) return 'Not configured'
    try {
        return new URL(trimmed).host || trimmed
    } catch {
        return trimmed
    }
}

function summarizeText(value: string | undefined, maxLength = 160): string | undefined {
    const trimmed = value?.trim()
    if (!trimmed) return undefined
    if (trimmed.length <= maxLength) return trimmed
    return `${trimmed.slice(0, maxLength - 1).trimEnd()}…`
}

function serializeHeaders(headers: Record<string, string>): string {
    return Object.entries(headers)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([name, value]) => `${name}: ${value}`)
        .join('\n')
}

function getMcpStatusRequest(
    entry: AgentMcpServerConfig | string | undefined,
    normalized: NormalizedAgentMcpServerConfig | undefined,
): McpStatusRequest | null {
    const rawUrl = getRawMcpServerUrl(entry)?.trim()
    const headers = getSecureBlobStatusHeaders(normalized?.headers)
    if (!rawUrl || !headers) return null

    const url = buildMcpStatusUrl({ url: rawUrl }, window.location.origin)
    if (!url) return null

    return {
        url,
        headers,
        requestKey: `${url}\n${serializeHeaders(headers)}`,
    }
}

async function fetchMcpStatusMetrics(request: McpStatusRequest): Promise<McpStatusFetchResult | undefined> {
    const response = await fetch(request.url, {
        headers: request.headers,
    })
    if (response.status === 429) {
        return { rateLimited: true }
    }
    if (!response.ok) {
        return undefined
    }

    const snapshot = await response.json() as McpStatusSnapshot
    const recentToolCalls = extractRecentToolCalls(snapshot)
    return {
        metrics: aggregateServerMetrics(snapshot),
        recentToolCalls: recentToolCalls.length > 0 ? recentToolCalls : undefined,
    }
}

function getQueuedMessagePreview(message: QueuedMessage, maxLength = 160): { label: string, title: string } {
    const textSummary = summarizeText(message.text, maxLength)
    const trimmedText = message.text.trim()
    if (textSummary && trimmedText) {
        return { label: textSummary, title: trimmedText }
    }

    const attachments = message.attachments ?? []
    if (attachments.length === 0) {
        return { label: 'Queued message', title: 'Queued message' }
    }

    const attachmentNames = attachments
        .map((attachment) => attachment.name?.trim())
        .filter((name): name is string => !!name)

    const baseLabel = attachments.length === 1 ? '1 attachment' : `${attachments.length} attachments`
    const detail = attachmentNames.length === 0
        ? baseLabel
        : attachments.length === 1
            ? `${baseLabel}: ${attachmentNames[0]}`
            : `${baseLabel}: ${attachmentNames[0]} +${attachments.length - 1} more`

    return {
        label: summarizeText(detail, maxLength) ?? baseLabel,
        title: attachmentNames.length > 0 ? attachmentNames.join(', ') : baseLabel,
    }
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${Math.max(0, Math.round(ms))}ms`
    if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`
    return `${Math.round(ms / 1000)}s`
}

function formatClock(timestamp: number): string {
    return new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(timestamp)
}

function formatMcpMetricsSummary(metrics: McpServerMetrics): string {
    const parts = [
        `${metrics.callCount} calls`,
        `${metrics.errorCount} errors`,
    ]
    if (metrics.latencyMsP50 > 0) {
        parts.push(`worst p50 ${metrics.latencyMsP50}ms`)
    }
    if (metrics.latencyMsP99 > 0) {
        parts.push(`worst p99 ${metrics.latencyMsP99}ms`)
    }
    parts.push(`${metrics.inflight} inflight`)
    if (metrics.activeSubscriptions > 0) {
        parts.push(`${metrics.activeSubscriptions} active subscription${metrics.activeSubscriptions !== 1 ? 's' : ''}`)
    }
    return parts.join(' · ')
}

function getBaseAgentPath(agentPath: string): string {
    return agentPath.includes('.agent.json.transcripts/')
        ? agentPath.replace(/\.agent\.json\.transcripts\/.*$/, '.agent.json')
        : agentPath
}

function getCurrentTranscriptPath(agentPath: string): string | null {
    if (!agentPath.includes('.agent.json.transcripts/')) return null
    return agentPath.endsWith('/transcript.json')
        ? agentPath
        : agentPath.endsWith('/')
            ? `${agentPath}transcript.json`
            : `${agentPath}/transcript.json`
}

function getTranscriptSessionTimestamp(path: string, fallbackTimestamp: number): number {
    const match = path.match(/\/session-(?:emb-)?(\d+)[^/]*\/transcript\.json$/)
    const parsed = match ? Number.parseInt(match[1], 10) : NaN
    return Number.isFinite(parsed) ? parsed : fallbackTimestamp
}

function formatTranscriptSessionLabel(timestamp: number): string {
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(timestamp)
}

function getWorkspaceToolStates(config: AgentConfig): WorkspaceToolState[] {
    return SECUREBLOB_TOOL_SETTINGS.map((tool) => ({
        ...tool,
        enabled: isSecureBlobToolEnabled(config, tool.name),
    }))
}

export function AgentPanel({ agentPath, workspace, onOpenFile, onEditConfig }: AgentPanelProps) {
    const agent = useAgent(agentPath, workspace)
    const baseAgentPath = useMemo(() => getBaseAgentPath(agentPath), [agentPath])
    const currentTranscriptPath = useMemo(() => getCurrentTranscriptPath(agentPath), [agentPath])
    const scrollRef = useRef<HTMLDivElement>(null)
    const transcriptContentRef = useRef<HTMLDivElement>(null)
    const bottomDockRef = useRef<HTMLDivElement>(null)
    const pinnedToBottomRef = useRef(true)
    const [userScrolledUp, setUserScrolledUp] = useState(false)
    const [showModelPicker, setShowModelPicker] = useState(false)
    const [showSettings, setShowSettings] = useState(false)
    const [apiKeyInput, setApiKeyInput] = useState<string | null>(null)
    const [apiBaseUrlInput, setApiBaseUrlInput] = useState<string | null>(null)
    const [apiModeInput, setApiModeInput] = useState<'chat-completions' | 'responses' | null>(null)
    const [thinkingLevelInput, setThinkingLevelInput] = useState<string | null>(null)
    const [mcpServers, setMcpServers] = useState<McpServerState[]>([])
    const [mcpRefreshNonce, setMcpRefreshNonce] = useState(0)
    const [showRunInspector, setShowRunInspector] = useState(false)
    const [showHistoryPanel, setShowHistoryPanel] = useState(false)
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
    const [expandedMcpCallHistory, setExpandedMcpCallHistory] = useState<string | null>(null)
    const [expandedMcpCallIndex, setExpandedMcpCallIndex] = useState<number | null>(null)
    const [expandedMcpEventStream, setExpandedMcpEventStream] = useState<Record<string, boolean>>({})
    const [bottomDockHeight, setBottomDockHeight] = useState(96)
    const [keyboardInset, setKeyboardInset] = useState(0)
    const [isCompactViewport, setIsCompactViewport] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
        return window.matchMedia('(max-width: 767px)').matches
    })
    const [showQueuedRail, setShowQueuedRail] = useState(false)
    const [isTranscriptMaximized, setIsTranscriptMaximized] = useState(false)
    const [now, setNow] = useState(() => Date.now())
    const [inputFocusRequestKey, setInputFocusRequestKey] = useState(0)
    const mcpStatusCacheRef = useRef<Map<string, CachedMcpServerStatus>>(new Map())

    const scrollTranscriptToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
        const container = scrollRef.current
        if (!container) return
        const top = Math.max(0, container.scrollHeight - container.clientHeight)
        if (typeof container.scrollTo === 'function') {
            container.scrollTo({ top, behavior })
            return
        }
        container.scrollTop = top
    }, [])

    // ── Auto-scroll logic ──────────────────────────────────────
    useLayoutEffect(() => {
        if (!pinnedToBottomRef.current) return
        scrollTranscriptToBottom()
    }, [agent.transcript, agent.streamingText, bottomDockHeight, scrollTranscriptToBottom])

    useEffect(() => {
        const container = scrollRef.current
        const content = transcriptContentRef.current
        if (!container || !content || typeof ResizeObserver === 'undefined') return

        const syncToBottom = () => {
            if (pinnedToBottomRef.current) {
                scrollTranscriptToBottom()
            }
        }

        const observer = new ResizeObserver(syncToBottom)
        observer.observe(content)
        observer.observe(container)

        const images = Array.from(content.querySelectorAll('img'))
        images.forEach((img) => img.addEventListener('load', syncToBottom))

        return () => {
            observer.disconnect()
            images.forEach((img) => img.removeEventListener('load', syncToBottom))
        }
    }, [agent.transcript, agent.streamingText, scrollTranscriptToBottom])

    useEffect(() => {
        pinnedToBottomRef.current = true
        setUserScrolledUp(false)
    }, [agentPath])

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

        const mediaQuery = window.matchMedia('(max-width: 767px)')
        const syncViewportMode = () => setIsCompactViewport(mediaQuery.matches)
        syncViewportMode()

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', syncViewportMode)
            return () => mediaQuery.removeEventListener('change', syncViewportMode)
        }

        mediaQuery.addListener(syncViewportMode)
        return () => mediaQuery.removeListener(syncViewportMode)
    }, [])

    useEffect(() => {
        if (!isCompactViewport || agent.queue.length <= 1) {
            setShowQueuedRail(false)
        }
    }, [isCompactViewport, agent.queue.length])

    useEffect(() => {
        const viewport = window.visualViewport
        if (!viewport) return

        let frameId = 0

        const syncViewportInset = () => {
            cancelAnimationFrame(frameId)
            frameId = window.requestAnimationFrame(() => {
                const nextInset = Math.max(0, Math.round(window.innerHeight - (viewport.height + viewport.offsetTop)))
                const keyboardHeight = nextInset > 56 ? nextInset : 0
                setKeyboardInset(keyboardHeight)

                if (keyboardHeight > 0 && pinnedToBottomRef.current) {
                    scrollTranscriptToBottom()
                }
            })
        }

        syncViewportInset()
        viewport.addEventListener('resize', syncViewportInset)
        viewport.addEventListener('scroll', syncViewportInset)

        return () => {
            cancelAnimationFrame(frameId)
            viewport.removeEventListener('resize', syncViewportInset)
            viewport.removeEventListener('scroll', syncViewportInset)
        }
    }, [scrollTranscriptToBottom])

    useEffect(() => {
        const dock = bottomDockRef.current
        if (!dock) return

        const handleFocusIn = () => {
            if (!pinnedToBottomRef.current) return
            window.requestAnimationFrame(() => scrollTranscriptToBottom('smooth'))
        }

        dock.addEventListener('focusin', handleFocusIn)
        return () => dock.removeEventListener('focusin', handleFocusIn)
    }, [scrollTranscriptToBottom])

    useEffect(() => {
        const dock = bottomDockRef.current
        if (!dock || typeof ResizeObserver === 'undefined') return

        const syncDockHeight = () => {
            setBottomDockHeight(Math.max(72, Math.ceil(dock.getBoundingClientRect().height)))
        }

        syncDockHeight()
        const observer = new ResizeObserver(syncDockHeight)
        observer.observe(dock)
        return () => observer.disconnect()
    }, [agent.queue.length])

    useEffect(() => {
        if (!showSettings || !agent.config) return
        setApiKeyInput(agent.config.provider.apiKey)
        setApiBaseUrlInput(agent.config.provider.baseUrl || '')
        setApiModeInput(getProviderApiMode(agent.config.provider))
        setThinkingLevelInput(agent.config.provider.reasoningEffort || '')
    }, [showSettings, agent.config])

    useEffect(() => {
        if (!showSettings || !agent.config) return

        const rawEntries = getMergedMcpEntries(agent.config)
        const normalizedEntries = getNormalizedMcpServers(agent.config)
        const entryNames = Object.keys(rawEntries)
        if (entryNames.length === 0) {
            setMcpServers([])
            return
        }

        let cancelled = false
        const previousStatusCache = mcpStatusCacheRef.current

        setMcpServers(entryNames.map((name) => {
            const entry = rawEntries[name]
            const normalized = normalizedEntries[name]
            const statusRequest = getMcpStatusRequest(entry, normalizedEntries[name])
            const cachedStatus = statusRequest ? previousStatusCache.get(name) : undefined
            return {
                name,
                enabled: isMcpServerEnabled(entry),
                proxy: isMcpProxyEnabled(entry),
                status: isMcpServerEnabled(entry) ? 'loading' : 'disabled',
                host: getDisplayHost(getRawMcpServerUrl(entry)),
                tools: getConfiguredToolStates(entry),
                metrics: cachedStatus && cachedStatus.requestKey === statusRequest?.requestKey ? cachedStatus.metrics : undefined,
                recentToolCalls: cachedStatus && cachedStatus.requestKey === statusRequest?.requestKey ? cachedStatus.recentToolCalls : undefined,
                statusRequestKey: statusRequest?.requestKey,
                liveEventsUrl: normalized?.url,
                liveEventsHeaders: normalized?.headers,
                supportsWorkspaceEventStream: false,
            }
        }))

        async function load() {
            const statusRequests = new Map<string, Promise<McpStatusFetchResult | undefined>>()
            const states = await Promise.all(entryNames.map(async (name): Promise<McpServerState> => {
                const rawEntry = rawEntries[name]
                const enabled = isMcpServerEnabled(rawEntry)
                if (!enabled) {
                    return {
                        name,
                        enabled: false,
                        proxy: isMcpProxyEnabled(rawEntry),
                        status: 'disabled',
                        host: getDisplayHost(getRawMcpServerUrl(rawEntry)),
                        tools: getConfiguredToolStates(rawEntry),
                        liveEventsUrl: normalizedEntries[name]?.url,
                        liveEventsHeaders: normalizedEntries[name]?.headers,
                        supportsWorkspaceEventStream: false,
                    }
                }

                const normalized = normalizedEntries[name]
                if (!normalized) {
                    return {
                        name,
                        enabled: true,
                        proxy: isMcpProxyEnabled(rawEntry),
                        status: 'error',
                        host: getDisplayHost(getRawMcpServerUrl(rawEntry)),
                        error: 'Missing or invalid MCP URL',
                        tools: getConfiguredToolStates(rawEntry),
                        supportsWorkspaceEventStream: false,
                    }
                }

                try {
                    const client = new RemoteMcpClient(name, normalized)
                    const tools = await client.listTools()
                    let metrics: McpServerMetrics | undefined
                    let recentToolCalls: McpToolCallEntry[] | undefined
                    const statusRequest = getMcpStatusRequest(rawEntry, normalized)
                    const cachedStatus = statusRequest ? previousStatusCache.get(name) : undefined
                    if (cachedStatus && cachedStatus.requestKey === statusRequest?.requestKey) {
                        metrics = cachedStatus.metrics
                        recentToolCalls = cachedStatus.recentToolCalls
                    }
                    if (statusRequest) {
                        let statusPromise = statusRequests.get(statusRequest.requestKey)
                        if (!statusPromise) {
                            statusPromise = fetchMcpStatusMetrics(statusRequest).catch(() => undefined)
                            statusRequests.set(statusRequest.requestKey, statusPromise)
                        }
                        const statusResult = await statusPromise
                        if (statusResult?.metrics) {
                            metrics = statusResult.metrics
                            recentToolCalls = statusResult.recentToolCalls
                        } else if (!statusResult?.rateLimited) {
                            metrics = undefined
                            recentToolCalls = undefined
                        }
                    }
                    return {
                        name,
                        enabled: true,
                        proxy: isMcpProxyEnabled(rawEntry),
                        status: 'ready',
                        host: getDisplayHost(normalized.upstreamUrl ?? normalized.url),
                        tools: tools.map((tool) => ({
                            name: tool.name,
                            title: tool.title,
                            description: tool.description,
                            enabled: isMcpToolEnabled(rawEntry, tool.name),
                        })),
                        metrics,
                        recentToolCalls,
                        statusRequestKey: statusRequest?.requestKey,
                        liveEventsUrl: normalized.url,
                        liveEventsHeaders: normalized.headers,
                        supportsWorkspaceEventStream: tools.some((tool) => tool.name === 'secureblob_workspace_subscribe_events'),
                    }
                } catch (error) {
                    return {
                        name,
                        enabled: true,
                        proxy: isMcpProxyEnabled(rawEntry),
                        status: 'error',
                        host: getDisplayHost(normalized.upstreamUrl ?? normalized.url),
                        error: error instanceof Error ? error.message : String(error),
                        tools: getConfiguredToolStates(rawEntry),
                        liveEventsUrl: normalized.url,
                        liveEventsHeaders: normalized.headers,
                        supportsWorkspaceEventStream: false,
                    }
                }
            }))

            if (!cancelled) {
                const nextStatusCache = new Map<string, CachedMcpServerStatus>()
                states.forEach((state) => {
                    if (state.metrics && state.statusRequestKey) {
                        nextStatusCache.set(state.name, {
                            requestKey: state.statusRequestKey,
                            metrics: state.metrics,
                            recentToolCalls: state.recentToolCalls,
                        })
                    }
                })
                mcpStatusCacheRef.current = nextStatusCache
                setMcpServers(states)
            }
        }

        load()
        return () => {
            cancelled = true
        }
    }, [showSettings, agent.config, mcpRefreshNonce])

    useEffect(() => {
        if (agent.status !== 'running' && agent.status !== 'stopping' && !agent.streamingText && !agent.isThinking) {
            return
        }

        const timer = window.setInterval(() => {
            setNow(Date.now())
        }, 400)

        return () => window.clearInterval(timer)
    }, [agent.status, agent.streamingText, agent.isThinking])

    const handleScroll = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60
        pinnedToBottomRef.current = atBottom
        setUserScrolledUp(!atBottom)
    }, [])

    const scrollToBottom = useCallback(() => {
        pinnedToBottomRef.current = true
        scrollTranscriptToBottom('smooth')
        setUserScrolledUp(false)
    }, [scrollTranscriptToBottom])

    const handleStartNewChat = useCallback(() => {
        setShowHistoryPanel(false)
        setShowRunInspector(false)
        setSelectedRunId(null)

        if (agentPath !== baseAgentPath && onOpenFile) {
            onOpenFile(baseAgentPath)
            return
        }

        agent.newRun()
        setInputFocusRequestKey((value) => value + 1)
    }, [agent, agentPath, baseAgentPath, onOpenFile])

    useEffect(() => {
        setInputFocusRequestKey((value) => value + 1)
    }, [agentPath])

    // ── Tool result lookup ─────────────────────────────────────
    const getToolResult = useCallback((toolId: string) => {
        return agent.transcript.find(
            (e): e is Extract<TranscriptEvent, { type: 'tool_result' }> =>
                e.type === 'tool_result' && e.id === toolId
        )
    }, [agent.transcript])

    // ── Files list for autocomplete ────────────────────────────
    const files = useMemo(() => workspace.fs.list(), [workspace])

    // ── Model display name ─────────────────────────────────────
    const modelDisplay = useMemo(() => {
        return agent.config?.model?.split('/').pop() || 'Choose model'
    }, [agent.config?.model])

    const providerHost = useMemo(() => getDisplayHost(agent.config?.provider.baseUrl), [agent.config?.provider.baseUrl])
    const providerApiMode = useMemo(() => getProviderApiMode(agent.config?.provider), [agent.config?.provider])
    const selectedApiMode = apiModeInput ?? providerApiMode
    const providerModeLabel = providerApiMode === 'responses' ? 'Responses' : 'Completions'
    const selectedApiModeLabel = selectedApiMode === 'responses' ? 'Responses API' : 'Chat Completions API'
    const selectedApiModeDescription = selectedApiMode === 'responses'
        ? 'Uses /responses with streaming, store=false, tool calls, and resumed-conversation replay with either reasoning items or assistant <thinking> tags when configured.'
        : 'Uses /chat/completions with streamed deltas for the widest compatibility across OpenAI-compatible providers.'
    const workspaceToolStates = useMemo(
        () => agent.config ? getWorkspaceToolStates(agent.config) : [],
        [agent.config],
    )
    const workspaceReadTools = useMemo(
        () => workspaceToolStates.filter((tool) => tool.mode === 'read'),
        [workspaceToolStates],
    )
    const workspaceWriteTools = useMemo(
        () => workspaceToolStates.filter((tool) => tool.mode === 'write'),
        [workspaceToolStates],
    )
    const workspaceToolsEnabled = useMemo(
        () => agent.config?.tools?.secureBlob?.enabled !== false,
        [agent.config],
    )
    const workspaceToolSummary = useMemo(() => ({
        totalTools: workspaceToolStates.length,
        enabledTools: workspaceToolStates.filter((tool) => tool.enabled).length,
    }), [workspaceToolStates])

    const mcpSummary = useMemo<McpSummary>(() => {
        const totalServers = mcpServers.length
        const enabledServers = mcpServers.filter((server) => server.enabled).length
        const readyServers = mcpServers.filter((server) => server.status === 'ready').length
        const loadingServers = mcpServers.filter((server) => server.status === 'loading').length
        const errorServers = mcpServers.filter((server) => server.status === 'error').length
        const totalTools = mcpServers.reduce((sum, server) => sum + server.tools.length, 0)
        const enabledTools = mcpServers.reduce((sum, server) => {
            return sum + server.tools.filter((tool) => tool.enabled).length
        }, 0)
        return {
            totalServers,
            enabledServers,
            readyServers,
            loadingServers,
            errorServers,
            totalTools,
            enabledTools,
        }
    }, [mcpServers])

    const configuredMcpEntries = useMemo(() => {
        return agent.config ? Object.entries(getMergedMcpEntries(agent.config)) : []
    }, [agent.config])

    const configuredMcpSummary = useMemo(() => {
        const total = configuredMcpEntries.length
        const enabled = configuredMcpEntries.filter(([, entry]) => isMcpServerEnabled(entry)).length
        return { total, enabled }
    }, [configuredMcpEntries])
    const configuredSpendLimit = agent.config?.limits?.maxSpendUsd ?? agent.config?.limits?.softSpendUsd ?? ''

    const showCostBadge = agent.currentCost.estimatedCostUsd > 0 || agent.spendWarning !== 'none'
    const canStartFreshRun = agent.transcript.length > 0 && agent.status !== 'running' && agent.status !== 'stopping'
    const queueIsCollapsible = isCompactViewport && agent.queue.length > 1
    const queueIsExpanded = !queueIsCollapsible || showQueuedRail
    const queuedItems = queueIsExpanded ? agent.queue : agent.queue.slice(0, 1)
    const hiddenQueueCount = Math.max(0, agent.queue.length - queuedItems.length)
    const hasRetryableUserMessage = useMemo(() => {
        const hasUserMessage = agent.transcript.some((event) => event.type === 'user_message')
        if (!hasUserMessage) return false
        if (agent.status === 'cancelled') return true
        if (agent.status !== 'error') return false
        const latestError = [...agent.transcript]
            .reverse()
            .find((event): event is Extract<TranscriptEvent, { type: 'error' }> => event.type === 'error')
        return latestError?.retryable !== false
    }, [agent.status, agent.transcript])

    const transcriptHistory = useMemo<TranscriptHistoryEntry[]>(() => {
        const runsPrefix = `${baseAgentPath}.transcripts/`
        return workspace.fs.list()
            .filter((file) => file.kind === 'file' && file.path.startsWith(runsPrefix) && file.path.endsWith('/transcript.json'))
            .sort((a, b) => b.mtime - a.mtime || b.path.localeCompare(a.path))
            .map((file, index) => {
                const startedAt = getTranscriptSessionTimestamp(file.path, file.mtime)
                return {
                    id: `${file.path}-${index}`,
                    path: file.path,
                    startedAt,
                    label: formatTranscriptSessionLabel(startedAt),
                    detail: currentTranscriptPath === file.path ? 'Current session' : `Open transcript · ${formatClock(startedAt)}`,
                    isCurrent: currentTranscriptPath === file.path,
                }
            })
    }, [workspace, baseAgentPath, currentTranscriptPath, agent.status, agent.transcript, agent.currentCost.estimatedCostUsd])

    const latestRunMetrics = useMemo((): { metrics: MessageMetrics | null; assistantIndex: number } => {
        let lastUserIndex = -1
        for (let i = agent.transcript.length - 1; i >= 0; i -= 1) {
            if (agent.transcript[i]?.type === 'user_message') {
                lastUserIndex = i
                break
            }
        }
        if (lastUserIndex < 0) {
            return { metrics: null, assistantIndex: -1 }
        }

        const runStart = agent.transcript[lastUserIndex].timestamp
        const followUps = agent.transcript.slice(lastUserIndex + 1)
        const firstActivity = followUps.find((event) => event.type !== 'cost_update' && event.type !== 'tool_result')

        let assistantIndex = -1
        for (let i = agent.transcript.length - 1; i > lastUserIndex; i -= 1) {
            if (agent.transcript[i]?.type === 'assistant_message_final') {
                assistantIndex = i
                break
            }
        }

        const endTimestamp = assistantIndex >= 0
            ? agent.transcript[assistantIndex].timestamp
            : agent.status === 'running' || agent.status === 'stopping'
                ? now
                : followUps[followUps.length - 1]?.timestamp ?? now

        const elapsedMs = Math.max(0, endTimestamp - runStart)
        const firstActivityMs = firstActivity ? Math.max(0, firstActivity.timestamp - runStart) : undefined
        const tokensOut = agent.currentCost.tokensOut > 0 ? agent.currentCost.tokensOut : undefined
        const tokensPerSecond = tokensOut && elapsedMs >= 1000
            ? tokensOut / (elapsedMs / 1000)
            : undefined

        return {
            assistantIndex,
            metrics: {
                startedAt: runStart,
                elapsedMs,
                firstActivityMs,
                tokensOut,
                tokensPerSecond,
            },
        }
    }, [agent.transcript, agent.status, agent.currentCost.tokensOut, now])

    const derivedRuns = useMemo<DerivedRun[]>(() => {
        interface WorkingRun {
            id: string
            prompt: string
            startedAt: number
            eventIndices: number[]
            rawEvents: TranscriptEvent[]
            assistantIndices: number[]
        }

        const grouped: WorkingRun[] = []
        let current: WorkingRun | null = null

        agent.transcript.forEach((event, index) => {
            if (event.type === 'user_message') {
                if (current) grouped.push(current)
                current = {
                    id: `run-${event.timestamp}-${index}`,
                    prompt: event.text,
                    startedAt: event.timestamp,
                    eventIndices: [index],
                    rawEvents: [event],
                    assistantIndices: [],
                }
                return
            }

            if (!current) return

            current.eventIndices.push(index)
            current.rawEvents.push(event)
            if (event.type === 'assistant_message_final') {
                current.assistantIndices.push(index)
            }
        })

        if (current) grouped.push(current)

        return grouped.map((run, runIndex) => {
            const isLatest = runIndex === grouped.length - 1
            const firstFollowUp = run.rawEvents.slice(1).find((event) => event.type !== 'cost_update' && event.type !== 'tool_result')
            const latestEvent = run.rawEvents[run.rawEvents.length - 1]
            const endedAt = isLatest && (agent.status === 'running' || agent.status === 'stopping')
                ? now
                : latestEvent?.timestamp ?? run.startedAt
            const elapsedMs = Math.max(0, endedAt - run.startedAt)
            const firstActivityMs = firstFollowUp ? Math.max(0, firstFollowUp.timestamp - run.startedAt) : undefined
            const tokensOut = isLatest && agent.currentCost.tokensOut > 0 ? agent.currentCost.tokensOut : undefined
            const tokensPerSecond = tokensOut && elapsedMs >= 1000
                ? tokensOut / (elapsedMs / 1000)
                : undefined

            let status: AgentRunStatus = 'completed'
            if (isLatest) {
                status = agent.status === 'idle' ? 'completed' : agent.status
            } else if (run.rawEvents.some((event) => event.type === 'error')) {
                status = 'error'
            }

            const toolResults = new Map(
                run.rawEvents
                    .filter((event): event is Extract<TranscriptEvent, { type: 'tool_result' }> => event.type === 'tool_result')
                    .map((event) => [event.id, event])
            )

            const timeline: RunTimelineItem[] = run.rawEvents.flatMap<RunTimelineItem>((event, eventIndex) => {
                switch (event.type) {
                    case 'user_message':
                        return [{
                            key: `${run.id}-prompt-${eventIndex}`,
                            kind: 'prompt',
                            timestamp: event.timestamp,
                            title: 'Prompt sent',
                            detail: summarizeText(event.text, 220),
                        }]
                    case 'assistant_thinking':
                        return [{
                            key: `${run.id}-thinking-${eventIndex}`,
                            kind: 'thinking',
                            timestamp: event.timestamp,
                            title: event.text ? 'Reasoning updated' : 'Reasoning started',
                            detail: summarizeText(event.text, 220),
                        }]
                    case 'tool_call': {
                        const result = toolResults.get(event.id)
                        const running = event.status === 'running' || event.status === 'pending'
                        return [{
                            key: `${run.id}-tool-${event.id}-${eventIndex}`,
                            kind: 'tool',
                            timestamp: event.timestamp,
                            title: event.name,
                            detail: summarizeText(JSON.stringify(event.args), 220),
                            status: event.status,
                            durationMs: result
                                ? Math.max(0, result.timestamp - event.timestamp)
                                : running && isLatest
                                    ? Math.max(0, now - event.timestamp)
                                    : undefined,
                        }]
                    }
                    case 'assistant_message_final':
                        return [{
                            key: `${run.id}-message-${eventIndex}`,
                            kind: 'message',
                            timestamp: event.timestamp,
                            title: 'Response ready',
                            detail: summarizeText(event.text, 240),
                        }]
                    case 'assistant_image':
                        return [{
                            key: `${run.id}-image-${eventIndex}`,
                            kind: 'image',
                            timestamp: event.timestamp,
                            title: 'Image generated',
                            detail: summarizeText(event.savedPath || 'Generated image', 160),
                        }]
                    case 'error':
                        return [{
                            key: `${run.id}-error-${eventIndex}`,
                            kind: 'error',
                            timestamp: event.timestamp,
                            title: 'Run error',
                            detail: summarizeText(event.message, 240),
                            status: event.retryable ? 'retryable' : 'fatal',
                        }]
                    default:
                        return []
                }
            })

            const fileChanges = run.rawEvents
                .filter((event): event is Extract<TranscriptEvent, { type: 'tool_result' }> => event.type === 'tool_result')
                .reduce((sum, event) => sum + (event.filesModified?.length ?? 0), 0)

            return {
                id: run.id,
                prompt: run.prompt,
                startedAt: run.startedAt,
                endedAt,
                status,
                metrics: {
                    startedAt: run.startedAt,
                    elapsedMs,
                    firstActivityMs,
                    tokensOut,
                    tokensPerSecond,
                },
                eventIndices: run.eventIndices,
                assistantIndices: run.assistantIndices,
                thinkingCount: run.rawEvents.filter((event) => event.type === 'assistant_thinking').length,
                toolCount: run.rawEvents.filter((event) => event.type === 'tool_call').length,
                errorCount: run.rawEvents.filter((event) => event.type === 'error').length,
                fileChanges,
                timeline,
                rawEvents: run.rawEvents,
            }
        })
    }, [agent.transcript, agent.status, agent.currentCost.tokensOut, now])

    const selectedRun = useMemo(
        () => derivedRuns.find((run) => run.id === selectedRunId) ?? derivedRuns[derivedRuns.length - 1] ?? null,
        [derivedRuns, selectedRunId],
    )
    const latestDerivedRunId = derivedRuns[derivedRuns.length - 1]?.id ?? null
    const showSelectedRunCost = !!selectedRun && selectedRun.id === latestDerivedRunId && showCostBadge

    useEffect(() => {
        if (!showRunInspector) return
        if (selectedRunId && derivedRuns.some((run) => run.id === selectedRunId)) return
        setSelectedRunId(derivedRuns[derivedRuns.length - 1]?.id ?? null)
    }, [showRunInspector, selectedRunId, derivedRuns])

    const updateMcpServer = useCallback((serverName: string, updater: (entry: AgentMcpServerConfig | string | undefined) => AgentMcpServerConfig) => {
        if (!agent.config) return
        const rootKey = getMcpRootKey(agent.config, serverName)
        const currentMap = { ...((agent.config[rootKey] as Record<string, AgentMcpServerConfig | string> | undefined) ?? {}) }
        currentMap[serverName] = updater(currentMap[serverName])
        agent.updateConfig({ [rootKey]: currentMap } as Partial<AgentConfig>)
    }, [agent])

    const handleToggleMcpServer = useCallback((serverName: string, enabled: boolean) => {
        setMcpServers((current) => current.map((server) => (
            server.name === serverName
                ? { ...server, enabled, status: enabled ? 'loading' : 'disabled' }
                : server
        )))
        updateMcpServer(serverName, (entry) => ({
            ...toEditableMcpServerConfig(entry),
            enabled,
        }))
    }, [updateMcpServer])

    const handleToggleMcpProxy = useCallback((serverName: string, proxy: boolean) => {
        setMcpServers((current) => current.map((server) => (
            server.name === serverName
                ? { ...server, proxy, status: server.enabled ? 'loading' : server.status }
                : server
        )))
        updateMcpServer(serverName, (entry) => ({
            ...toEditableMcpServerConfig(entry),
            proxy,
        }))
    }, [updateMcpServer])

    const handleToggleMcpTool = useCallback((serverName: string, toolName: string, enabled: boolean) => {
        setMcpServers((current) => current.map((server) => (
            server.name !== serverName
                ? server
                : {
                    ...server,
                    tools: server.tools.map((tool) => tool.name === toolName ? { ...tool, enabled } : tool),
                }
        )))
        updateMcpServer(serverName, (entry) => {
            const next = toEditableMcpServerConfig(entry)
            const currentTool = next.tools?.[toolName]
            next.tools = {
                ...(next.tools ?? {}),
                [toolName]: typeof currentTool === 'object'
                    ? { ...currentTool, enabled }
                    : { enabled },
            }
            return next
        })
    }, [updateMcpServer])

    const handleToggleTranscriptMaximized = useCallback(() => {
        setIsTranscriptMaximized((previous) => {
            const next = !previous
            if (next) {
                setShowRunInspector(false)
                setShowHistoryPanel(false)
                setShowSettings(false)
            }
            return next
        })
    }, [])

    const handleToggleWorkspaceTools = useCallback((enabled: boolean) => {
        if (!agent.config) return
        agent.updateConfig({
            tools: {
                ...(agent.config.tools ?? {}),
                secureBlob: {
                    ...(agent.config.tools?.secureBlob ?? {}),
                    enabled,
                },
            },
        })
    }, [agent])

    const handleToggleWorkspaceTool = useCallback((toolName: string, enabled: boolean) => {
        if (!agent.config) return
        const secureBlobConfig = agent.config.tools?.secureBlob ?? {}
        const currentTool = secureBlobConfig.tools?.[toolName]
        agent.updateConfig({
            tools: {
                ...(agent.config.tools ?? {}),
                secureBlob: {
                    ...secureBlobConfig,
                    tools: {
                        ...(secureBlobConfig.tools ?? {}),
                        [toolName]: typeof currentTool === 'object'
                            ? { ...currentTool, enabled }
                            : { enabled },
                    },
                },
            },
        })
    }, [agent])

    // ── Status display ─────────────────────────────────────────
    const statusIcon = useMemo(() => {
        switch (agent.status) {
            case 'running': return '●'
            case 'stopping': return '◌'
            case 'completed': return '✓'
            case 'error': return '✗'
            case 'cancelled': return '○'
            default: return ''
        }
    }, [agent.status])

    // ── Error state ────────────────────────────────────────────
    if (agent.configError) {
        return (
            <div className="agent-panel agent-error-state">
                <div className="agent-error-content">
                    <span className="agent-error-icon">⚠️</span>
                    <h3>Invalid Agent Configuration</h3>
                    <p>{agent.configError}</p>
                    <button className="btn btn-primary" onClick={onEditConfig}>
                        Edit Config
                    </button>
                </div>
            </div>
        )
    }

    if (!agent.config) {
        return (
            <div className="agent-panel agent-loading">
                <div className="agent-loading-spinner" />
                <span>Loading agent…</span>
            </div>
        )
    }

    return (
        <div
            className={`agent-panel${isTranscriptMaximized ? ' transcript-maximized' : ''}`}
            style={{
                '--agent-dock-height': `${bottomDockHeight}px`,
                '--agent-keyboard-inset': `${keyboardInset}px`,
            } as CSSProperties}
        >
            {/* ── Header ────────────────────────────────────── */}
            <header className="agent-header">
                <div className="agent-header-top">
                    <div className="agent-header-brand">
                        <span className="agent-avatar">🤖</span>
                        <div className="agent-title-stack">
                            <span className="agent-name">{agent.config.name}</span>
                            <div className="agent-header-meta">
                                <span className="agent-meta-chip" title={agent.config.provider.baseUrl || 'No API base URL configured'}>
                                    API · {providerHost}
                                </span>
                                <span className="agent-meta-chip" title="Streaming API mode">
                                    Mode · {providerModeLabel}
                                </span>
                                {agent.config.provider.reasoningEffort && (
                                    <span className="agent-meta-chip" title="Configured thinking level">
                                        Thinking · {agent.config.provider.reasoningEffort}
                                    </span>
                                )}
                                {workspaceToolSummary.totalTools > 0 && (
                                    <span className="agent-meta-chip" title={`${workspaceToolSummary.enabledTools}/${workspaceToolSummary.totalTools} workspace tools enabled`}>
                                        Tools · {workspaceToolSummary.enabledTools}/{workspaceToolSummary.totalTools}
                                    </span>
                                )}
                                {configuredMcpSummary.total > 0 && (
                                    <span
                                        className={`agent-meta-chip${mcpSummary.errorServers > 0 ? ' agent-meta-chip-error' : ''}`}
                                        title={mcpSummary.totalServers > 0
                                            ? `${mcpSummary.enabledTools}/${mcpSummary.totalTools} MCP tools enabled`
                                            : `${configuredMcpSummary.enabled}/${configuredMcpSummary.total} MCP servers enabled`}
                                    >
                                        {mcpSummary.totalServers > 0
                                            ? `MCP · ${mcpSummary.readyServers}/${mcpSummary.enabledServers || mcpSummary.totalServers} ready`
                                            : `MCP · ${configuredMcpSummary.enabled}/${configuredMcpSummary.total} on`}
                                    </span>
                                )}
                                {!agent.config.model && (
                                    <span className="agent-meta-chip agent-meta-chip-warning">Model required</span>
                                )}
                            </div>
                            {agent.status !== 'idle' && (
                                <div className={`agent-mini-status status-${agent.status}`}>
                                    <span className="status-icon">{statusIcon}</span>
                                    <span className="status-text">
                                        {agent.status === 'running' ? 'Generating…' :
                                            agent.status === 'stopping' ? 'Stopping…' :
                                                agent.status === 'completed' ? 'Response ready' :
                                                    agent.status === 'cancelled' ? 'Stopped' :
                                                        agent.status === 'error' ? 'Error' : ''}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="agent-header-spacer" />

                    <div className="agent-header-actions">
                        {canStartFreshRun && (
                            <button
                                className="agent-model-pill agent-action-primary"
                                onClick={handleStartNewChat}
                                title="Start a new chat"
                            >
                                <span className="model-name">New chat</span>
                            </button>
                        )}

                        <button
                            className={`agent-model-pill agent-model-selector${agent.config.model ? '' : ' model-pill-missing'}`}
                            onClick={() => setShowModelPicker(!showModelPicker)}
                            title="Change model"
                        >
                            <span className="model-name">{modelDisplay}</span>
                            <span className="model-chevron">▾</span>
                        </button>

                        {transcriptHistory.length > 0 && (
                            <button
                                className={`agent-header-tool agent-run-inspector-trigger${showHistoryPanel ? ' model-item-active' : ''}`}
                                onClick={() => {
                                    setShowRunInspector(false)
                                    setShowHistoryPanel((value) => !value)
                                }}
                                title="Open transcript history"
                                aria-label={`Open transcript history (${transcriptHistory.length})`}
                            >
                                <History size={16} strokeWidth={2} />
                                <span className="agent-tool-count">{transcriptHistory.length}</span>
                            </button>
                        )}

                        {derivedRuns.length > 0 && (
                            <button
                                className={`agent-header-tool agent-run-inspector-trigger${showRunInspector ? ' model-item-active' : ''}`}
                                onClick={() => {
                                    setShowHistoryPanel(false)
                                    setSelectedRunId(derivedRuns[derivedRuns.length - 1]?.id ?? null)
                                    setShowRunInspector((value) => !value)
                                }}
                                title="Open conversation details"
                                aria-label="Open conversation details"
                            >
                                <PanelRightOpen size={16} strokeWidth={2} />
                            </button>
                        )}

                        <div className="header-divider" />

                        <button
                            className={`agent-header-tool agent-header-tool-muted${isTranscriptMaximized ? ' model-item-active' : ''}`}
                            onClick={handleToggleTranscriptMaximized}
                            title={isTranscriptMaximized ? 'Restore layout' : 'Maximize chat area'}
                            aria-label={isTranscriptMaximized ? 'Restore chat area' : 'Maximize chat area'}
                        >
                            {isTranscriptMaximized ? <Minimize2 size={15} strokeWidth={2} /> : <Maximize2 size={15} strokeWidth={2} />}
                        </button>
                        <button
                            className={`agent-header-tool agent-header-tool-muted${showSettings ? ' model-item-active' : ''}`}
                            onClick={() => setShowSettings(!showSettings)}
                            title="Settings"
                            aria-label="Agent settings"
                        >
                            <Settings2 size={15} strokeWidth={2} />
                        </button>
                        <button
                            className="agent-header-tool agent-header-tool-muted"
                            onClick={onEditConfig}
                            title="Edit raw config"
                            aria-label="Edit raw config"
                        >
                            <FilePenLine size={15} strokeWidth={2} />
                        </button>
                    </div>
                </div>

                {/* Status bar */}
                {agent.status !== 'idle' && (
                    <div className={`agent-status-bar status-${agent.status}`}>
                        <span className="status-icon">{statusIcon}</span>
                        <span className="status-text">
                            {agent.status === 'running' ? 'Generating…' :
                                agent.status === 'stopping' ? 'Stopping…' :
                                    agent.status === 'completed' ? 'Completed' :
                                        agent.status === 'cancelled' ? 'Cancelled' :
                                            agent.status === 'error' ? 'Error' : ''}
                        </span>
                        {latestRunMetrics.metrics && (
                            <TranscriptMetricsBar metrics={latestRunMetrics.metrics} />
                        )}
                        {hasRetryableUserMessage && (
                            <button
                                type="button"
                                className="btn btn-ghost agent-inline-action agent-status-action"
                                onClick={agent.retryLastMessage}
                                title="Retry last message"
                                aria-label="Retry last message"
                            >
                                Retry
                            </button>
                        )}
                    </div>
                )}

                {/* Model picker */}
                {showModelPicker && (
                    <ModelPicker
                        currentModel={agent.config?.model ?? ''}
                        baseUrl={agent.config?.provider.baseUrl}
                        apiKey={agent.config?.provider.apiKey}
                        onSelect={(modelId) => { agent.changeModel(modelId); setShowModelPicker(false) }}
                        onClose={() => setShowModelPicker(false)}
                    />
                )}

                {/* Settings panel */}
                {showSettings && (
                    <div className="agent-settings-panel">
                        <section className="settings-section">
                            <div className="settings-section-header">
                                <div className="settings-section-copy">
                                    <h3 className="settings-section-title">Provider</h3>
                                    <p className="settings-section-description">
                                        Connection and compatibility settings for the upstream OpenAI-compatible API.
                                    </p>
                                </div>
                                <div className="settings-section-chips">
                                    <span className="mcp-summary-chip">Host {providerHost}</span>
                                    <span className="mcp-summary-chip">Mode {providerModeLabel}</span>
                                </div>
                            </div>
                            <div className="settings-field">
                                <label>API Key</label>
                                <div className="settings-row">
                                    <input
                                        type="password"
                                        className="settings-input"
                                        placeholder="Enter API key"
                                        value={apiKeyInput ?? agent.config.provider.apiKey}
                                        onChange={e => setApiKeyInput(e.target.value)}
                                    />
                                    {apiKeyInput !== null && apiKeyInput !== agent.config.provider.apiKey && (
                                        <button
                                            className="btn btn-primary settings-save"
                                            onClick={() => { agent.updateApiKey(apiKeyInput); setApiKeyInput(null) }}
                                        >
                                            Save
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="settings-field">
                                <label>API Base URL</label>
                                <div className="settings-row">
                                    <input
                                        type="url"
                                        className="settings-input"
                                        placeholder="https://provider.example/v1"
                                        value={apiBaseUrlInput ?? agent.config.provider.baseUrl ?? ''}
                                        onChange={e => setApiBaseUrlInput(e.target.value)}
                                    />
                                    {apiBaseUrlInput !== null && apiBaseUrlInput !== (agent.config.provider.baseUrl || '') && (
                                        <button
                                            className="btn btn-primary settings-save"
                                            onClick={() => {
                                                agent.updateConfig({
                                                    provider: { ...agent.config!.provider, baseUrl: apiBaseUrlInput },
                                                })
                                                setApiBaseUrlInput(null)
                                            }}
                                        >
                                            Save
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="settings-field">
                                <label>API Mode</label>
                                <div className="settings-row">
                                    <select
                                        aria-label="API Mode"
                                        className="settings-input"
                                        value={selectedApiMode}
                                        onChange={(e) => setApiModeInput(e.target.value as 'chat-completions' | 'responses')}
                                    >
                                        <option value="chat-completions">Streamed Chat Completions</option>
                                        <option value="responses">Streamed Responses API</option>
                                    </select>
                                    {apiModeInput !== null && apiModeInput !== providerApiMode && (
                                        <button
                                            className="btn btn-primary settings-save"
                                            onClick={() => {
                                                agent.updateConfig({
                                                    provider: {
                                                        ...agent.config!.provider,
                                                        apiMode: apiModeInput,
                                                    },
                                                })
                                                setApiModeInput(null)
                                            }}
                                        >
                                            Save
                                        </button>
                                    )}
                                </div>
                                <p className="settings-help">
                                    <strong>{selectedApiModeLabel}.</strong> {selectedApiModeDescription}
                                </p>
                            </div>
                            <div className="settings-field">
                                <label>Responses Proxy</label>
                                <div className="settings-row">
                                    <label className="settings-checkbox">
                                        <input
                                            type="checkbox"
                                            aria-label="Enable responses proxy"
                                            checked={agent.config.provider.useProxy === true}
                                            onChange={() => agent.toggleProxy()}
                                        />
                                        <span>Enable proxy for agent responses</span>
                                    </label>
                                </div>
                                <p className="settings-help">
                                    Proxies Responses API traffic through the SecureBlob backend. Useful for bypassing CORS or network restrictions.
                                </p>
                            </div>
                        </section>
                        <section className="settings-section">
                            <div className="settings-section-header">
                                <div className="settings-section-copy">
                                    <h3 className="settings-section-title">Reasoning & Limits</h3>
                                    <p className="settings-section-description">
                                        Tune how hard the model thinks and what spend guardrail this agent should use.
                                    </p>
                                </div>
                                <div className="settings-section-chips">
                                    <span className="mcp-summary-chip">
                                        Thinking {agent.config.provider.reasoningEffort ?? 'default'}
                                    </span>
                                    {configuredSpendLimit && (
                                        <span className="mcp-summary-chip">Limit ${configuredSpendLimit}</span>
                                    )}
                                </div>
                            </div>
                            <div className="settings-field">
                                <label>Thinking Level</label>
                                <div className="settings-row">
                                    <select
                                        aria-label="Thinking Level"
                                        className="settings-input"
                                        value={thinkingLevelInput ?? agent.config.provider.reasoningEffort ?? ''}
                                        onChange={(e) => setThinkingLevelInput(e.target.value)}
                                    >
                                        <option value="">Provider default</option>
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                    {thinkingLevelInput !== null && thinkingLevelInput !== (agent.config.provider.reasoningEffort ?? '') && (
                                        <button
                                            className="btn btn-primary settings-save"
                                            onClick={() => {
                                                agent.updateConfig({
                                                    provider: {
                                                        ...agent.config!.provider,
                                                        reasoningEffort: thinkingLevelInput ? (thinkingLevelInput as 'low' | 'medium' | 'high') : undefined,
                                                    },
                                                })
                                                setThinkingLevelInput(null)
                                            }}
                                        >
                                            Save
                                        </button>
                                    )}
                                </div>
                                <p className="settings-help">
                                    Responses mode also preserves reasoning state for resumed runs; completions mode falls back to ordinary streamed deltas.
                                </p>
                            </div>
                            <div className="settings-field">
                                <label>Responses Replay</label>
                                <label className="settings-checkbox">
                                    <input
                                        type="checkbox"
                                        aria-label="Replay reasoning as think tags"
                                        checked={agent.config.provider.responsesReplayReasoningAsThinkingTokens === true}
                                        onChange={(e) => {
                                            agent.updateConfig({
                                                provider: {
                                                    ...agent.config!.provider,
                                                    responsesReplayReasoningAsThinkingTokens: e.target.checked || undefined,
                                                },
                                            })
                                        }}
                                    />
                                    <span>
                                        Replay prior reasoning as <code>&lt;think&gt;...&lt;/think&gt;</code> inside assistant messages
                                    </span>
                                </label>
                                <p className="settings-help">
                                    Only affects resumed Responses requests. Incoming reasoning is still streamed and stored normally; this changes how prior reasoning is sent back to providers that cannot consume standalone reasoning items.
                                </p>
                            </div>
                            <div className="settings-field">
                                <label>Spend limit (USD)</label>
                                <input
                                    type="text"
                                    className="settings-input"
                                    value={configuredSpendLimit}
                                    onChange={e => agent.updateConfig({
                                        limits: { ...agent.config?.limits, maxSpendUsd: e.target.value }
                                    })}
                                />
                                <p className="settings-help">
                                    Applies the agent&apos;s configured spend guardrail for future runs.
                                </p>
                            </div>
                        </section>

                        <section className="settings-section">
                            <div className="settings-section-header">
                                <div className="settings-section-copy">
                                    <h3 className="settings-section-title">Workspace Tools</h3>
                                    <p className="settings-section-description">
                                        Local SecureBlob tools. Disabled tools are removed from the request entirely, so the model cannot see or call them.
                                    </p>
                                </div>
                                <div className="settings-section-chips">
                                    <span className="mcp-summary-chip">
                                        Model sees {workspaceToolSummary.enabledTools}/{workspaceToolSummary.totalTools}
                                    </span>
                                    <label className="mcp-toggle workspace-tool-master-toggle">
                                        <input
                                            aria-label="Show workspace tools to model"
                                            type="checkbox"
                                            checked={workspaceToolsEnabled}
                                            onChange={(e) => handleToggleWorkspaceTools(e.target.checked)}
                                        />
                                        <span>{workspaceToolsEnabled ? 'Visible to model' : 'Hidden from model'}</span>
                                    </label>
                                </div>
                            </div>
                            <div className="mcp-settings-list">
                                <div className="mcp-server-card">
                                    <div className="mcp-server-header">
                                        <div className="mcp-server-title">
                                            <span className={`mcp-server-indicator ${workspaceToolsEnabled ? 'mcp-indicator-ready' : 'mcp-indicator-disabled'}`} />
                                            <div className="mcp-server-name-stack">
                                                <span className="mcp-server-name">secureblob workspace tools</span>
                                                <span className="mcp-server-meta">
                                                    {workspaceToolsEnabled
                                                        ? `${workspaceToolSummary.enabledTools}/${workspaceToolSummary.totalTools} tools visible to the model`
                                                        : 'All local workspace tools hidden from the model'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mcp-tool-list workspace-tool-groups">
                                        <div className="workspace-tool-group">
                                            <div className="workspace-tool-group-header">
                                                <span className="workspace-tool-group-title">Read tools</span>
                                                <span className="mcp-summary-chip">
                                                    {workspaceReadTools.filter((tool) => tool.enabled).length}/{workspaceReadTools.length} visible
                                                </span>
                                            </div>
                                            {workspaceReadTools.map((tool) => (
                                                <label key={tool.name} className={`mcp-tool-row${workspaceToolsEnabled ? '' : ' disabled'}`}>
                                                    <div className="mcp-tool-text">
                                                        <div className="workspace-tool-name-row">
                                                            <span className="mcp-tool-name">{tool.title}</span>
                                                            <span className="workspace-tool-badge">Read</span>
                                                        </div>
                                                        <span className="mcp-tool-id">{tool.name}</span>
                                                        <span className="mcp-tool-description">{tool.description}</span>
                                                    </div>
                                                    <input
                                                        aria-label={`Enable ${tool.title}`}
                                                        type="checkbox"
                                                        checked={tool.enabled}
                                                        disabled={!workspaceToolsEnabled}
                                                        onChange={(e) => handleToggleWorkspaceTool(tool.name, e.target.checked)}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                        <div className="workspace-tool-group">
                                            <div className="workspace-tool-group-header">
                                                <span className="workspace-tool-group-title">Write tools</span>
                                                <span className="mcp-summary-chip">
                                                    {workspaceWriteTools.filter((tool) => tool.enabled).length}/{workspaceWriteTools.length} visible
                                                </span>
                                            </div>
                                            {workspaceWriteTools.map((tool) => (
                                                <label key={tool.name} className={`mcp-tool-row${workspaceToolsEnabled ? '' : ' disabled'}`}>
                                                    <div className="mcp-tool-text">
                                                        <div className="workspace-tool-name-row">
                                                            <span className="mcp-tool-name">{tool.title}</span>
                                                            <span className="workspace-tool-badge workspace-tool-badge-write">Write</span>
                                                        </div>
                                                        <span className="mcp-tool-id">{tool.name}</span>
                                                        <span className="mcp-tool-description">{tool.description}</span>
                                                    </div>
                                                    <input
                                                        aria-label={`Enable ${tool.title}`}
                                                        type="checkbox"
                                                        checked={tool.enabled}
                                                        disabled={!workspaceToolsEnabled}
                                                        onChange={(e) => handleToggleWorkspaceTool(tool.name, e.target.checked)}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="settings-section">
                            <div className="settings-section-header">
                                <div className="settings-section-copy">
                                    <h3 className="settings-section-title">MCP Servers</h3>
                                    <p className="settings-section-description">
                                        External tool surfaces. Disabled servers or tools are omitted from the request and stay invisible to the model.
                                    </p>
                                </div>
                                {mcpServers.length > 0 && (
                                    <div className="settings-section-chips">
                                        <span className="mcp-summary-chip">Ready {mcpSummary.readyServers}</span>
                                        <span className="mcp-summary-chip">Loading {mcpSummary.loadingServers}</span>
                                        <span className={`mcp-summary-chip${mcpSummary.errorServers > 0 ? ' mcp-summary-chip-error' : ''}`}>Errors {mcpSummary.errorServers}</span>
                                        <span className="mcp-summary-chip">Tools {mcpSummary.enabledTools}/{mcpSummary.totalTools} enabled</span>
                                    </div>
                                )}
                            </div>
                            {mcpServers.length > 0 && (
                                <div className="mcp-settings-summary">
                                    <button
                                        className="btn btn-ghost mcp-refresh-button"
                                        onClick={() => setMcpRefreshNonce((value) => value + 1)}
                                        type="button"
                                    >
                                        Refresh
                                    </button>
                                </div>
                            )}
                            <div className="mcp-settings-list">
                                {mcpServers.length === 0 && (
                                    <div className="mcp-empty-state">No MCP servers configured in this agent.</div>
                                )}
                                {mcpServers.map((server) => (
                                    <div key={server.name} className={`mcp-server-card mcp-status-${server.status}`}>
                                        <div className="mcp-server-header">
                                            <div className="mcp-server-title">
                                                <span className={`mcp-server-indicator mcp-indicator-${server.status}`} />
                                                <div className="mcp-server-name-stack">
                                                    <span className="mcp-server-name">{server.name}</span>
                                                    <span className="mcp-server-meta">
                                                        {server.status === 'ready'
                                                            ? `${server.tools.filter((tool) => tool.enabled).length}/${server.tools.length} tools enabled`
                                                            : server.status === 'loading'
                                                                ? 'Loading tools…'
                                                                : server.status === 'disabled'
                                                                    ? 'Disabled'
                                                                    : server.error || 'Unavailable'}
                                                    </span>
                                                    {server.host && (
                                                        <span className="mcp-server-host">{server.host}</span>
                                                    )}
                                                    <span className="mcp-server-host">
                                                        {server.proxy ? 'Proxy via SecureBlob' : 'Direct browser connection'}
                                                    </span>
                                                    {server.metrics && (
                                                        <span className="mcp-server-metrics">
                                                            {formatMcpMetricsSummary(server.metrics)}
                                                        </span>
                                                    )}
                                                    {server.recentToolCalls && server.recentToolCalls.length > 0 && (
                                                        <button
                                                            className="btn btn-ghost mcp-call-history-button"
                                                            onClick={() => setExpandedMcpCallHistory(expandedMcpCallHistory === server.name ? null : server.name)}
                                                            type="button"
                                                        >
                                                            {expandedMcpCallHistory === server.name ? 'Hide history' : `View ${server.recentToolCalls.length} call${server.recentToolCalls.length === 1 ? '' : 's'}`}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mcp-toggle-group">
                                                <label className="mcp-toggle">
                                                    <input
                                                        aria-label={`Use SecureBlob proxy for ${server.name}`}
                                                        type="checkbox"
                                                        checked={server.proxy}
                                                        onChange={(e) => handleToggleMcpProxy(server.name, e.target.checked)}
                                                    />
                                                    <span>{server.proxy ? 'Proxy enabled' : 'Direct connection'}</span>
                                                </label>
                                                <label className="mcp-toggle">
                                                    <input
                                                        aria-label={`Enable MCP server ${server.name}`}
                                                        type="checkbox"
                                                        checked={server.enabled}
                                                        onChange={(e) => handleToggleMcpServer(server.name, e.target.checked)}
                                                    />
                                                    <span>{server.enabled ? 'Enabled' : 'Disabled'}</span>
                                                </label>
                                            </div>
                                        </div>
                                        {server.tools.length > 0 && (
                                            <div className="mcp-tool-list">
                                                {server.tools.map((tool) => (
                                                    <label key={tool.name} className={`mcp-tool-row${server.enabled ? '' : ' disabled'}`}>
                                                        <div className="mcp-tool-text">
                                                            <span className="mcp-tool-name">{tool.title || tool.name}</span>
                                                            {tool.title && <span className="mcp-tool-id">{tool.name}</span>}
                                                            {tool.description && <span className="mcp-tool-description">{tool.description}</span>}
                                                        </div>
                                                        <input
                                                            aria-label={`Enable ${tool.title || tool.name} on ${server.name}`}
                                                            type="checkbox"
                                                            checked={tool.enabled}
                                                            disabled={!server.enabled}
                                                            onChange={(e) => handleToggleMcpTool(server.name, tool.name, e.target.checked)}
                                                        />
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                        {server.error && (
                                            <div className="mcp-server-error">
                                                <span className="mcp-server-error-label">Connection detail</span>
                                                <code>{server.error}</code>
                                            </div>
                                        )}
                                        {/* §276 — Live Events stream (collapsed by default) */}
                                        <div className="mcp-event-stream-toggle">
                                            <button
                                                className="btn btn-ghost mcp-event-stream-toggle-btn"
                                                type="button"
                                                onClick={() => setExpandedMcpEventStream(prev => ({
                                                    ...prev,
                                                    [server.name]: !prev[server.name],
                                                }))}
                                                aria-expanded={!!expandedMcpEventStream[server.name]}
                                                aria-label={`Toggle live events for ${server.name}`}
                                            >
                                                {expandedMcpEventStream[server.name] ? 'Hide live events' : 'Live events'}
                                            </button>
                                        </div>
                                        {expandedMcpEventStream[server.name] && (
                                                <McpEventStream
                                                    endpointUrl={server.liveEventsUrl}
                                                    headers={server.liveEventsHeaders}
                                                    proxy={server.proxy}
                                                    supportsWorkspaceEventStream={server.supportsWorkspaceEventStream === true}
                                                />
                                        )}
                                        {expandedMcpCallHistory === server.name && server.recentToolCalls && server.recentToolCalls.length > 0 && (
                                            <div className="mcp-call-history">
                                                <div className="mcp-call-history-header">Recent tool calls</div>
                                                {server.recentToolCalls.map((call, index) => {
                                                    const isExpanded = expandedMcpCallHistory === server.name && expandedMcpCallIndex === index;
                                                    return (
                                                        <div
                                                            key={index}
                                                            className={`mcp-call-history-row mcp-call-status-${call.status.toLowerCase()}${isExpanded ? ' mcp-call-row-expanded' : ''}`}
                                                            onClick={() => {
                                                                if (expandedMcpCallHistory !== server.name) {
                                                                    setExpandedMcpCallHistory(server.name)
                                                                    setExpandedMcpCallIndex(index)
                                                                } else {
                                                                    setExpandedMcpCallIndex(expandedMcpCallIndex === index ? null : index)
                                                                }
                                                            }}
                                                        >
                                                            <div className="mcp-call-row-main">
                                                                <span className="mcp-call-tool">{call.toolName}</span>
                                                                <span className="mcp-call-meta">
                                                                    {call.status}
                                                                    {call.durationMs > 0 && <> · {call.durationMs}ms</>}
                                                                    {call.startedAtUtc && <> · {new Date(call.startedAtUtc).toLocaleTimeString()}</>}
                                                                    {(call.requestSummary || call.responseSummary) && (
                                                                        <span className="mcp-call-has-summary"> · details</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            {isExpanded && (
                                                                <>
                                                                    {call.requestSummary && (
                                                                        <div className="mcp-call-request-summary">
                                                                            <span className="mcp-call-summary-label">Request: </span>
                                                                            {call.requestSummary}
                                                                        </div>
                                                                    )}
                                                                    {call.responseSummary && (
                                                                        <div className="mcp-call-response-summary">
                                                                            <span className="mcp-call-summary-label">Response: </span>
                                                                            {call.responseSummary}
                                                                        </div>
                                                                    )}
                                                                    {call.errorCode && (
                                                                        <div className="mcp-call-error">
                                                                            {call.errorCode}{call.errorMessage ? `: ${call.errorMessage}` : ''}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                            {!isExpanded && call.errorCode && (
                                                                <div className="mcp-call-error">
                                                                    {call.errorCode}{call.errorMessage ? `: ${call.errorMessage}` : ''}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                )}
            </header>

            {/* ── Transcript stream ─────────────────────────── */}
            {isTranscriptMaximized && (
                <button
                    type="button"
                    className="agent-transcript-exit"
                    onClick={handleToggleTranscriptMaximized}
                    title="Restore layout"
                    aria-label="Restore chat area"
                >
                    <Minimize2 size={16} strokeWidth={2} />
                </button>
            )}
            <div className="agent-transcript" ref={scrollRef} onScroll={handleScroll}>
                <div className="agent-transcript-content" ref={transcriptContentRef}>
                    {agent.transcript.length === 0 && agent.status === 'idle' && (
                        <div className="agent-empty-state">
                            <div className="empty-avatar">🤖</div>
                            <h3>{agent.config.name}</h3>
                            <p>
                                {agent.config.model
                                    ? <>Ask me anything about your workspace. Use <code>@/path</code> to reference files.</>
                                    : 'Choose a model first, then start chatting. The sample agent config leaves model selection open on purpose.'}
                            </p>
                        </div>
                    )}

                    {agent.transcript.map((event, index) => (
                        <TranscriptMessage
                            key={index}
                            event={event}
                            now={now}
                            messageMetrics={index === latestRunMetrics.assistantIndex ? latestRunMetrics.metrics ?? undefined : undefined}
                            getToolResult={getToolResult}
                            onOpenFile={onOpenFile}
                            onPromoteImage={agent.promoteImage}
                            isPromotingImage={agent.isImagePromotionPending(event)}
                            isThinkingActive={agent.isThinking && event.type === 'assistant_thinking' && index === agent.transcript.length - 1}
                        />
                    ))}

                    {/* ── MCP SSE live event stream component ───────── */}
                    {/* Rendered inline where needed in settings panel */}

                    {/* Live streaming text (not yet in transcript) */}
                    {agent.streamingText && (
                        <div className="transcript-item transcript-assistant transcript-streaming">
                            <div className="assistant-content">
                                <MarkdownContent text={agent.streamingText} />
                                {latestRunMetrics.metrics && (
                                    <TranscriptMetricsBar metrics={latestRunMetrics.metrics} />
                                )}
                                <span className="streaming-cursor" />
                            </div>
                        </div>
                    )}

                    {/* Spend warning banner */}
                    {agent.spendWarning === 'exceeded' && (
                        <div className="transcript-item spend-warning-banner">
                            ⚠️ Spend limit reached (${agent.currentCost.estimatedCostUsd.toFixed(2)})
                        </div>
                    )}
                </div>
            </div>

            {/* Jump to bottom FAB */}
            {userScrolledUp && (
                <button className="agent-jump-bottom" onClick={scrollToBottom} title="Jump to bottom">
                    ↓
                </button>
            )}

            {showRunInspector && selectedRun && (
                <>
                    <button
                        type="button"
                        className="agent-run-inspector-backdrop"
                        aria-label="Close conversation details"
                        onClick={() => setShowRunInspector(false)}
                    />
                    <aside className="agent-run-inspector" aria-label="Conversation details">
                        <div className="run-inspector-header">
                            <div>
                                <div className="run-inspector-title-row">
                                    <h3>Conversation Details</h3>
                                    <span className={`run-status-badge run-status-${selectedRun.status}`}>
                                        {selectedRun.status}
                                    </span>
                                </div>
                                <p>{summarizeText(selectedRun.prompt, 220) || 'No prompt text'}</p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost agent-inline-action"
                                onClick={() => setShowRunInspector(false)}
                                title="Close conversation details"
                            >
                                Close
                            </button>
                        </div>

                        {derivedRuns.length > 1 && (
                            <div className="run-inspector-switcher">
                                {derivedRuns.slice().reverse().map((run, reverseIndex) => (
                                    <button
                                        key={run.id}
                                        type="button"
                                        className={`run-inspector-tab${run.id === selectedRun.id ? ' active' : ''}`}
                                        onClick={() => setSelectedRunId(run.id)}
                                    >
                                        <span>Turn {derivedRuns.length - reverseIndex}</span>
                                        <small>{formatClock(run.startedAt)}</small>
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="run-inspector-stats">
                            <span className="run-stat-chip">Started {formatClock(selectedRun.startedAt)}</span>
                            <span className="run-stat-chip">Elapsed {formatDuration(selectedRun.metrics.elapsedMs)}</span>
                            {selectedRun.metrics.firstActivityMs !== undefined && (
                                <span className="run-stat-chip">First activity {formatDuration(selectedRun.metrics.firstActivityMs)}</span>
                            )}
                            <span className="run-stat-chip">Thinking {selectedRun.thinkingCount}</span>
                            <span className="run-stat-chip">Tools {selectedRun.toolCount}</span>
                            <span className="run-stat-chip">Files {selectedRun.fileChanges}</span>
                            {selectedRun.errorCount > 0 && (
                                <span className="run-stat-chip run-stat-chip-error">Errors {selectedRun.errorCount}</span>
                            )}
                        </div>

                        {showSelectedRunCost && (
                            <div className="run-inspector-subtle-meta">
                                <span className="run-subtle-label">Spend</span>
                                <CostBadge
                                    cost={agent.currentCost}
                                    limit={agent.config.limits?.maxSpendUsd ?? agent.config.limits?.softSpendUsd}
                                    warning={agent.spendWarning}
                                />
                                <span className="run-subtle-chip">In {agent.currentCost.tokensIn}</span>
                                <span className="run-subtle-chip">Out {agent.currentCost.tokensOut}</span>
                            </div>
                        )}

                        <div className="run-inspector-timeline">
                            {selectedRun.timeline.map((item) => (
                                <div key={item.key} className={`run-timeline-item run-kind-${item.kind}`}>
                                    <div className="run-timeline-marker" />
                                    <div className="run-timeline-body">
                                        <div className="run-timeline-topline">
                                            <span className="run-timeline-title">{item.title}</span>
                                            <span className="run-timeline-time">{formatClock(item.timestamp)}</span>
                                        </div>
                                        {(item.detail || item.status || item.durationMs !== undefined) && (
                                            <div className="run-timeline-meta">
                                                {item.detail && <span className="run-timeline-detail">{item.detail}</span>}
                                                {item.status && <span className="run-timeline-chip">{item.status}</span>}
                                                {item.durationMs !== undefined && (
                                                    <span className="run-timeline-chip">{formatDuration(item.durationMs)}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <details className="run-inspector-raw">
                            <summary>Raw run events</summary>
                            <pre>{JSON.stringify(selectedRun.rawEvents, null, 2)}</pre>
                        </details>
                    </aside>
                </>
            )}

            {showHistoryPanel && (
                <>
                    <button
                        type="button"
                        className="agent-run-inspector-backdrop"
                        aria-label="Close transcript history"
                        onClick={() => setShowHistoryPanel(false)}
                    />
                    <aside className="agent-run-inspector" aria-label="Transcript history">
                        <div className="run-inspector-header">
                            <div>
                                <div className="run-inspector-title-row">
                                    <h3>History</h3>
                                </div>
                                <p>Open an earlier transcript to review or resume that chat session.</p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost agent-inline-action"
                                onClick={() => setShowHistoryPanel(false)}
                                title="Close transcript history"
                            >
                                Close
                            </button>
                        </div>

                        <div className="run-inspector-switcher">
                            {transcriptHistory.map((session) => (
                                <button
                                    key={session.id}
                                    type="button"
                                    className={`run-inspector-tab${session.isCurrent ? ' active' : ''}`}
                                    disabled={!session.isCurrent && !onOpenFile}
                                    onClick={() => {
                                        if (session.isCurrent) {
                                            setShowHistoryPanel(false)
                                            return
                                        }
                                        onOpenFile?.(session.path)
                                        setShowHistoryPanel(false)
                                    }}
                                >
                                    <span>{session.label}</span>
                                    <small>{session.detail}</small>
                                </button>
                            ))}
                        </div>
                    </aside>
                </>
            )}

            <div className="agent-bottom-dock" ref={bottomDockRef}>
                <div className="agent-bottom-dock-inner">
                    {/* Queue display */}
                    {agent.queue.length > 0 && (
                        <div className="agent-queue" aria-live="polite">
                            <div className="agent-queue-header">
                                <div className="agent-queue-summary">
                                    <span className="agent-queue-title">Queued next</span>
                                    <span className="agent-queue-count">{agent.queue.length}</span>
                                </div>
                                <div className="agent-queue-actions">
                                    <span className="agent-queue-hint">Sends automatically after the current reply, or interrupt to send one now.</span>
                                    {queueIsCollapsible && queueIsExpanded && (
                                        <button
                                            type="button"
                                            className="agent-queue-toggle expanded"
                                            onClick={() => setShowQueuedRail(false)}
                                            aria-expanded={queueIsExpanded}
                                        >
                                            Collapse
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="agent-queue-list" role="list" aria-label="Queued messages">
                                {queuedItems.map((q, index) => {
                                    const preview = getQueuedMessagePreview(q, queueIsExpanded ? 132 : 150)
                                    return (
                                    <div
                                        key={q.id}
                                        className={`queue-item${index === 0 ? ' queue-item-next' : ''}`}
                                        role="listitem"
                                    >
                                        <span className="queue-order" aria-hidden="true">{index + 1}</span>
                                        <span className="queue-text" title={preview.title}>
                                            {preview.label}
                                        </span>
                                        <div className="queue-item-actions">
                                            <button
                                                className="queue-prioritize"
                                                onClick={() => agent.prioritizeQueueItem(q.id)}
                                                title={index === 0
                                                    ? (agent.status === 'running' || agent.status === 'stopping'
                                                        ? 'Interrupt current reply and send this next'
                                                        : 'Send this queued message now')
                                                    : 'Move this queued message up'}
                                                aria-label={`Prioritize queued message ${index + 1}`}
                                            >
                                                <ArrowUp size={14} />
                                            </button>
                                            <button
                                                className="queue-remove"
                                                onClick={() => agent.removeFromQueue(q.id)}
                                                title="Remove from queue"
                                                aria-label={`Remove queued message ${index + 1}`}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                    )
                                })}
                                {!queueIsExpanded && hiddenQueueCount > 0 && (
                                    <button
                                        type="button"
                                        className="queue-more-card"
                                        onClick={() => setShowQueuedRail(true)}
                                        aria-label={`Show ${hiddenQueueCount} more queued messages`}
                                    >
                                        <span className="queue-more-count">+{hiddenQueueCount}</span>
                                        <span className="queue-more-label">more queued</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Input area ────────────────────────────────── */}
                    <FileRefInput
                        onSend={agent.sendMessage}
                        onStop={agent.stopRun}
                        isRunning={agent.status === 'running'}
                        isStopping={agent.status === 'stopping'}
                        files={files}
                        queueLength={agent.queue.length}
                        focusRequestKey={inputFocusRequestKey}
                    />
                </div>
            </div>
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/* §276 — MCP SSE live event stream component                                 */
/* -------------------------------------------------------------------------- */

interface McpEventStreamProps {
    endpointUrl?: string
    headers?: Record<string, string>
    proxy: boolean
    supportsWorkspaceEventStream: boolean
}

interface McpStreamEvent {
    id: string
    timestamp: number
    type: string
    summary: string
}

const MAX_STREAM_EVENTS = 50
const MCP_PROTOCOL_VERSION = '2025-06-18'
const WORKSPACE_EVENT_KINDS = [
    'workspace:sync-status',
    'workspace:external-change',
    'workspace:deleted',
    'componentjob:run-completed',
] as const

function readRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null
}

function buildMcpTransportHeaders(
    baseHeaders: Record<string, string> | undefined,
    sessionId?: string,
    accept = 'application/json, text/event-stream',
): HeadersInit {
    return {
        Accept: accept,
        'Content-Type': 'application/json',
        'MCP-Protocol-Version': MCP_PROTOCOL_VERSION,
        ...(baseHeaders ?? {}),
        ...(sessionId ? { 'MCP-Session-Id': sessionId } : {}),
    }
}

function findJsonRpcMessage(payload: unknown, requestId: string): Record<string, unknown> {
    const messages = Array.isArray(payload) ? payload : [payload]
    for (const message of messages) {
        const candidate = readRecord(message)
        if (!candidate) continue
        if (candidate.id === requestId && ('result' in candidate || 'error' in candidate)) {
            return candidate
        }
    }

    throw new Error(`MCP server returned no response for request ${requestId}`)
}

function findEventBoundary(buffer: string): number {
    const unix = buffer.indexOf('\n\n')
    const windows = buffer.indexOf('\r\n\r\n')
    if (unix === -1) return windows
    if (windows === -1) return unix
    return Math.min(unix, windows)
}

function eventBoundaryLength(buffer: string, index: number): number {
    return buffer.startsWith('\r\n\r\n', index) ? 4 : 2
}

function parseSseEventPayload(rawEvent: string): unknown | null {
    const dataLines: string[] = []
    for (const line of rawEvent.replace(/\r/g, '').split('\n')) {
        if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trimStart())
        }
    }
    if (dataLines.length === 0) return null

    try {
        return JSON.parse(dataLines.join('\n'))
    } catch {
        return null
    }
}

class StaleMcpSessionError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'StaleMcpSessionError'
    }
}

function isRecoverableStaleMcpSession(
    status: number,
    errorText: string,
    sessionId?: string,
): boolean {
    return !!sessionId
        && status === 404
        && /Unknown MCP session/i.test(errorText)
}

async function readJsonRpcResponse(response: Response, requestId: string): Promise<Record<string, unknown>> {
    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
        return findJsonRpcMessage(await response.json(), requestId)
    }

    if (contentType.includes('text/event-stream')) {
        if (!response.body) {
            throw new Error(`MCP server closed before replying to ${requestId}`)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
            const { value, done } = await reader.read()
            buffer += decoder.decode(value || new Uint8Array(), { stream: !done })

            let boundary = findEventBoundary(buffer)
            while (boundary >= 0) {
                const rawEvent = buffer.slice(0, boundary)
                buffer = buffer.slice(boundary + eventBoundaryLength(buffer, boundary))
                const payload = parseSseEventPayload(rawEvent)
                if (payload !== null) {
                    return findJsonRpcMessage(payload, requestId)
                }
                boundary = findEventBoundary(buffer)
            }

            if (done) break
        }
    }

    const text = await response.text().catch(() => '')
    throw new Error(`Unsupported MCP response type: ${contentType || 'unknown'} ${text}`.trim())
}

async function sendMcpRequest(
    endpointUrl: string,
    headers: Record<string, string> | undefined,
    requestId: string,
    method: string,
    params: Record<string, unknown> | undefined,
    signal: AbortSignal,
    sessionId?: string,
): Promise<{ message: Record<string, unknown>; sessionId: string | null }> {
    const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: buildMcpTransportHeaders(headers, sessionId),
        body: JSON.stringify({
            jsonrpc: '2.0',
            id: requestId,
            method,
            ...(params ? { params } : {}),
        }),
        signal,
    })

    if (!response.ok) {
        const errorText = await response.text().catch(() => '')
        if (isRecoverableStaleMcpSession(response.status, errorText, sessionId)) {
            throw new StaleMcpSessionError(`HTTP ${response.status}: ${errorText || response.statusText}`.trim())
        }
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`.trim())
    }

    const message = await readJsonRpcResponse(response, requestId)
    const error = readRecord(message.error)
    if (error && typeof error.message === 'string') {
        throw new Error(error.message)
    }

    return {
        message,
        sessionId: response.headers.get('MCP-Session-Id'),
    }
}

async function sendMcpNotification(
    endpointUrl: string,
    headers: Record<string, string> | undefined,
    method: string,
    signal: AbortSignal,
    sessionId?: string,
): Promise<void> {
    const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: buildMcpTransportHeaders(headers, sessionId),
        body: JSON.stringify({
            jsonrpc: '2.0',
            method,
            params: {},
        }),
        signal,
    })

    if (!response.ok && response.status !== 202) {
        const errorText = await response.text().catch(() => '')
        if (isRecoverableStaleMcpSession(response.status, errorText, sessionId)) {
            throw new StaleMcpSessionError(`HTTP ${response.status}: ${errorText || response.statusText}`.trim())
        }
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`.trim())
    }
}

function summarizeWorkspaceEvent(
    kind: string,
    workspaceRef: string | null,
    payload: Record<string, unknown> | null,
): string {
    const prefix = workspaceRef ? `${workspaceRef}: ` : ''
    const message = typeof payload?.message === 'string' ? payload.message : null

    switch (kind) {
        case 'workspace:sync-status': {
            const state = typeof payload?.state === 'string' ? payload.state : 'updated'
            return `${prefix}${state}`
        }
        case 'workspace:external-change':
            return `${prefix}${message ?? 'external change detected'}`
        case 'workspace:deleted':
            return `${prefix}${message ?? 'workspace deleted'}`
        case 'componentjob:run-completed': {
            const runId = typeof payload?.runId === 'string' ? payload.runId : 'run'
            return message ?? `${prefix}${runId} completed`
        }
        default:
            return message ?? JSON.stringify(payload ?? { kind }).slice(0, 120)
    }
}

function parseMcpStreamEvent(payload: unknown): McpStreamEvent | null {
    const message = readRecord(payload)
    const params = readRecord(message?.params)
    const payloadBody = readRecord(params?.payload)

    if (typeof message?.method === 'string' && message.method === 'workspace/event' && typeof params?.kind === 'string') {
        const timestampValue = typeof params.timestamp === 'string'
            ? Date.parse(params.timestamp)
            : typeof params.timestamp === 'number'
                ? params.timestamp
                : Date.now()
        const timestamp = Number.isFinite(timestampValue) ? timestampValue : Date.now()
        const kind = params.kind
        const workspaceRef = typeof params.workspaceRef === 'string' ? params.workspaceRef : null
        const runId = typeof payloadBody?.runId === 'string' ? payloadBody.runId : null

        return {
            id: typeof payloadBody?.eventId === 'string'
                ? payloadBody.eventId
                : `${kind}-${runId ?? workspaceRef ?? timestamp}-${Math.random().toString(36).slice(2, 8)}`,
            timestamp,
            type: kind,
            summary: summarizeWorkspaceEvent(kind, workspaceRef, payloadBody),
        }
    }

    const fallbackType = typeof message?.method === 'string'
        ? message.method
        : typeof message?.type === 'string'
            ? message.type
            : typeof message?.event === 'string'
                ? message.event
                : null
    if (!fallbackType) return null

    return {
        id: typeof message?.id === 'string'
            ? message.id
            : `${fallbackType}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        timestamp: Date.now(),
        type: fallbackType,
        summary: JSON.stringify(message).slice(0, 120),
    }
}

function McpEventStream({ endpointUrl, headers, proxy, supportsWorkspaceEventStream }: McpEventStreamProps) {
    const [events, setEvents] = useState<McpStreamEvent[]>([])
    const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected'>('connecting')
    const [error, setError] = useState<string | null>(null)
    const unsupportedReason = !supportsWorkspaceEventStream
        ? 'This MCP server does not expose SecureBlob workspace live events.'
        : proxy
            ? 'Live events currently require a direct SecureBlob MCP endpoint; proxy transport only forwards POST MCP calls.'
            : !endpointUrl
                ? 'Missing MCP endpoint URL.'
                : null

    useEffect(() => {
        if (unsupportedReason || !endpointUrl) {
            return
        }

        const streamUrl = endpointUrl
        const controller = new AbortController()
        setEvents([])
        setConnectionState('connecting')
        setError(null)

        let mounted = true

        async function connect() {
            let allowSessionRecovery = true
            try {
                while (mounted) {
                    try {
                        const initializeResponse = await sendMcpRequest(
                            streamUrl,
                            headers,
                            'mcp-event-stream-initialize',
                            'initialize',
                            {
                                protocolVersion: MCP_PROTOCOL_VERSION,
                                capabilities: {},
                                clientInfo: {
                                    name: 'SecureBlob AgentPanel',
                                    version: '0.1.0',
                                },
                            },
                            controller.signal,
                        )
                        const sessionId = initializeResponse.sessionId?.trim()
                        if (!sessionId) {
                            throw new Error('MCP initialize did not return an MCP-Session-Id header')
                        }

                        await sendMcpNotification(
                            streamUrl,
                            headers,
                            'notifications/initialized',
                            controller.signal,
                            sessionId,
                        )

                        await sendMcpRequest(
                            streamUrl,
                            headers,
                            'mcp-event-stream-subscribe',
                            'tools/call',
                            {
                                name: 'secureblob_workspace_subscribe_events',
                                arguments: {
                                    eventKinds: [...WORKSPACE_EVENT_KINDS],
                                },
                            },
                            controller.signal,
                            sessionId,
                        )

                        const res = await fetch(streamUrl, {
                            method: 'GET',
                            headers: buildMcpTransportHeaders(headers, sessionId, 'text/event-stream'),
                            signal: controller.signal,
                        })

                        if (!res.ok || !res.body) {
                            const errorText = await res.text().catch(() => '')
                            if (isRecoverableStaleMcpSession(res.status, errorText, sessionId)) {
                                throw new StaleMcpSessionError(`HTTP ${res.status}: ${errorText || res.statusText}`.trim())
                            }
                            setError(`HTTP ${res.status}: ${errorText || res.statusText}`.trim())
                            return
                        }

                        setConnectionState('connected')

                        const reader = res.body.getReader()
                        const decoder = new TextDecoder()
                        let buffer = ''

                        while (true) {
                            const { done, value } = await reader.read()
                            if (!mounted) break

                            buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
                            let boundary = findEventBoundary(buffer)

                            while (boundary >= 0) {
                                const rawEvent = buffer.slice(0, boundary)
                                buffer = buffer.slice(boundary + eventBoundaryLength(buffer, boundary))
                                const payload = parseSseEventPayload(rawEvent)
                                const parsed = payload ? parseMcpStreamEvent(payload) : null
                                if (parsed) {
                                    setEvents(prev => {
                                        const next = [parsed, ...prev]
                                        if (next.length > MAX_STREAM_EVENTS) {
                                            return next.slice(0, MAX_STREAM_EVENTS)
                                        }
                                        return next
                                    })
                                }
                                boundary = findEventBoundary(buffer)
                            }

                            if (done) break
                        }

                        return
                    } catch (err: unknown) {
                        if (allowSessionRecovery && err instanceof StaleMcpSessionError) {
                            allowSessionRecovery = false
                            continue
                        }
                        throw err
                    }
                }
            } catch (err: unknown) {
                if (mounted && !(err instanceof Error && err.name === 'AbortError')) {
                    setError(err instanceof Error ? err.message : 'Connection failed')
                }
            } finally {
                if (mounted) setConnectionState('disconnected')
            }
        }

        connect()

        return () => {
            mounted = false
            controller.abort()
        }
    }, [endpointUrl, headers, supportsWorkspaceEventStream, proxy, unsupportedReason])

    if (unsupportedReason) {
        return (
            <div className="mcp-event-stream">
                <div className="mcp-event-stream-header">
                    <span className="mcp-event-stream-status mcp-event-status-disconnected">○ Unavailable</span>
                    <span className="mcp-event-stream-error">{unsupportedReason}</span>
                    <span className="mcp-event-stream-count">0 events</span>
                </div>
                <div className="mcp-event-stream-body">
                    <div className="mcp-event-stream-empty">Waiting for a direct workspace event stream…</div>
                </div>
            </div>
        )
    }

    if (connectionState === 'connecting' && !error && events.length === 0) {
        return <div className="mcp-event-stream-connecting">Connecting…</div>
    }

    const connected = connectionState === 'connected'

    return (
        <div className="mcp-event-stream">
            <div className="mcp-event-stream-header">
                <span className={`mcp-event-stream-status mcp-event-status-${connected ? 'connected' : 'disconnected'}`}>
                    {connected ? '● Connected' : '○ Disconnected'}
                </span>
                {error && <span className="mcp-event-stream-error">{error}</span>}
                <span className="mcp-event-stream-count">{events.length} events</span>
            </div>
            <div className="mcp-event-stream-body">
                {events.length === 0 && (
                    <div className="mcp-event-stream-empty">Waiting for events…</div>
                )}
                {events.map((evt) => (
                    <div key={evt.id} className="mcp-event-stream-entry">
                        <span className="mcp-event-stream-time">
                            {new Date(evt.timestamp).toLocaleTimeString()}
                        </span>
                        <span className="mcp-event-stream-type">{evt.type}</span>
                        <span className="mcp-event-stream-summary">{evt.summary}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}
